import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { errorMessage } from '../api/client';
import type { CategoryNode, RoomDocument, SetBy, SetDocumentDatingRequest } from '../api/types';
import {
  categoryParents,
  categoryPathsById,
  deepestCategoryId,
  pathIdsTo,
} from '../lib/category-tree';
import { formatDateOnly } from '../lib/date-only';
import { noteGuideActivity } from '../lib/first-run-guide';
import {
  PERIOD_OPTIONS,
  dateFieldSuggestions,
  isNeedsReview,
  setByLabel,
  suggestionCertainty,
  type DateFieldSuggestion,
} from '../lib/document-filing';
import { withoutInstitutionLevel } from '../lib/new-category';
import { cn } from '../lib/utils';
import { AddCategoryForm, type AddCategoryMutation } from './add-category-form';
import { Button } from './button';
import { CategoryPicker } from './category-picker';
import { Dialog } from './dialog';
import { Icon } from './icons';

/** The part of an app's react-query mutation the dialog reads. */
export interface ManageMutation<V, R = unknown> {
  mutate: (variables: V, options?: { onSuccess?: (result: R) => void }) => void;
  isPending: boolean;
  error: unknown;
  reset: () => void;
}

/** What the admin app adds: the AI's pending date suggestions, to Confirm or Reject. */
export interface ManageDocumentSuggestions {
  suggestions: ReturnType<typeof dateFieldSuggestions>;
  loading: boolean;
  approve: ManageMutation<string>;
  reject: ManageMutation<string>;
}

/**
 * The app's data hooks, handed in so ONE dialog serves the room admin and the
 * seller side (owner decision 2026-10-21) without the shared kit owning a
 * query client. Each is a stable module-level function (called unconditionally,
 * as any hook). `useSuggestions` is the admin's alone (confirming an AI
 * suggestion is its route); `useAddCategory` ("Add a new subfolder…") and
 * `useReclassify` ("Re-file with AI") are offered where the app passes them.
 */
export interface ManageDocumentHooks {
  useCategories: (roomId: string) => {
    data: CategoryNode[] | undefined;
    isPending: boolean;
    error: unknown;
  };
  useFile: (
    roomId: string,
  ) => ManageMutation<
    { documentId: string; body: { categoryTagIds: string[] } },
    { similarDocuments?: { filing: number } }
  >;
  useDating: (
    roomId: string,
  ) => ManageMutation<{ documentId: string; body: SetDocumentDatingRequest }>;
  useSuggestions?: (roomId: string, documentId: string) => ManageDocumentSuggestions;
  useAddCategory?: (roomId: string) => AddCategoryMutation;
  useReclassify?: (roomId: string) => ManageMutation<string>;
}

export interface ManageDocumentDialogProps {
  roomId: string;
  /** The LIVE document (the caller re-reads it from the polled listing), so the
   * "Currently" summary shows each change once it lands. */
  document: RoomDocument;
  onClose: () => void;
  hooks: ManageDocumentHooks;
  /** The room admin's "put it in more than one folder": hands over to the checkbox
   * dialog of category tags (this dialog files in ONE place). None → no link. */
  onEditTags?: () => void;
  /** A "?" help tip shown after the "Change the date" heading. */
  dateHeadingAside?: ReactNode;
}

const NO_SUGGESTIONS: ManageDocumentSuggestions = {
  suggestions: { period: [], date: [] },
  loading: false,
  approve: idleMutation(),
  reject: idleMutation(),
};
function idleMutation<V, R = unknown>(): ManageMutation<V, R> {
  return { mutate: () => undefined, isPending: false, error: null, reset: () => undefined };
}
const useNoSuggestions = (): ManageDocumentSuggestions => NO_SUGGESTIONS;
const useNoReclassify = (): ManageMutation<string> => NO_SUGGESTIONS.approve;
const IDLE_ADD: AddCategoryMutation = { mutate: () => undefined, isPending: false, error: null };
const useNoAddCategory = (): AddCategoryMutation => IDLE_ADD;

const controlClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/** A person's edit of one field, made against the value it had then. Once the
 * live document's value moves on (the save landed, or an AI suggestion was
 * confirmed), the edit no longer applies and the control shows the new value. */
interface FieldEdit {
  from: string;
  to: string;
}
const editedValue = (edit: FieldEdit | null, now: string): string =>
  edit && edit.from === now ? edit.to : now;

/**
 * "Manage document" (owner ask 2026-10-15): one place, opened from the top of
 * the viewer, to SEE where a document is filed and under what date, and to
 * change them.
 *
 *   - Currently: the full filing path and the period / date of data. A value the
 *     AI left only as a suggestion says so ("AI suggestion — confirm").
 *   - Date: the period (a cadence: Annual, Quarterly…) and the date of data (the
 *     last day the data covers). A person sets or clears either; "Save date"
 *     sends only what changed (PUT …/dating, recorded as the person's decision).
 *     The AI's pending suggestions sit under each field — Confirm (one click) or
 *     Reject — through the classification approve / reject endpoints. (Above
 *     Filing: it is short.)
 *   - "Set by AI" / "Set by a person" beside each current value says who set it.
 *   - Filing: the room's category tree with in-place search; "File in this
 *     branch" sits at the top, grey until a category is chosen. It replaces the
 *     document's categories with that branch, through the same category PATCH
 *     the review panel's "Wrong branch?" uses (recorded as the person's decision).
 *
 * Every save re-reads the listing (the hooks invalidate it), so the viewer's
 * header line and the documents list update; the dialog stays open and says
 * what changed.
 */
export function ManageDocumentDialog({
  roomId,
  document,
  onClose,
  hooks,
  onEditTags,
  dateHeadingAside,
}: ManageDocumentDialogProps) {
  const categories = hooks.useCategories(roomId);
  const modify = hooks.useFile(roomId);
  const dating = hooks.useDating(roomId);
  const {
    suggestions,
    loading: suggestionsLoading,
    approve,
    reject,
  } = (hooks.useSuggestions ?? useNoSuggestions)(roomId, document.id);
  const reclassify = (hooks.useReclassify ?? useNoReclassify)(roomId);
  const addCategory = (hooks.useAddCategory ?? useNoAddCategory)(roomId);
  const canReclassify = Boolean(hooks.useReclassify);
  const canAddCategory = Boolean(hooks.useAddCategory);
  const [adding, setAdding] = useState(false);
  const [addBusy, setAddBusy] = useState(false);

  const nodes = useMemo(() => categories.data ?? [], [categories.data]);
  const paths = useMemo(() => categoryPathsById(nodes), [nodes]);
  const parentOf = useMemo(() => categoryParents(nodes), [nodes]);

  const [branch, setBranch] = useState<string | null>(null);
  // What the last save did, announced and focused once it lands.
  const [saved, setSaved] = useState<string | null>(null);
  const savedRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (saved) savedRef.current?.focus();
  }, [saved]);

  // A first-run guide ticks "Move a document or fix its date" once this has been opened.
  useEffect(() => noteGuideActivity('manage-document'), []);

  const filingId = useId();
  const dateId = useId();
  const periodInputId = useId();
  const dataDateInputId = useId();
  const dataDateHintId = useId();

  const busy =
    modify.isPending ||
    approve.isPending ||
    reject.isPending ||
    dating.isPending ||
    reclassify.isPending ||
    addBusy;
  const error =
    errorMessage(modify.error) ??
    errorMessage(approve.error) ??
    errorMessage(reject.error) ??
    errorMessage(dating.error) ??
    errorMessage(reclassify.error);
  const resetActions = () => {
    modify.reset();
    approve.reset();
    reject.reset();
    dating.reset();
    reclassify.reset();
    setSaved(null);
  };

  // The date controls show the live value unless the person has changed it.
  const periodNow = document.period ?? '';
  const dataDateNow = (document.dataDate ?? '').slice(0, 10);
  const [periodEdit, setPeriodEdit] = useState<FieldEdit | null>(null);
  const [dataDateEdit, setDataDateEdit] = useState<FieldEdit | null>(null);
  const periodValue = editedValue(periodEdit, periodNow);
  const dataDateValue = editedValue(dataDateEdit, dataDateNow);
  const periodChanged = periodValue !== periodNow;
  const dataDateChanged = dataDateValue !== dataDateNow;
  const canSaveDate = (periodChanged || dataDateChanged) && !busy;
  // Sends ONLY the fields the person changed; an emptied field clears (null).
  const saveDate = () => {
    const body: SetDocumentDatingRequest = {};
    const said: string[] = [];
    if (periodChanged) {
      body.period = periodValue ? (periodValue as SetDocumentDatingRequest['period']) : null;
      said.push(periodValue ? `Period set to ${periodValue}.` : 'Period cleared.');
    }
    if (dataDateChanged) {
      body.dataDate = dataDateValue || null;
      said.push(
        dataDateValue
          ? `Date of data set to ${formatDateOnly(dataDateValue)}.`
          : 'Date of data cleared.',
      );
    }
    resetActions();
    dating.mutate({ documentId: document.id, body }, { onSuccess: () => setSaved(said.join(' ')) });
  };

  // Currently filed: the deepest category's full path, plus any category off
  // that path (a document may be filed in two places).
  const filedIds = document.categoryTagIds ?? [];
  const deepest = deepestCategoryId(filedIds, parentOf);
  const onDeepestPath = new Set(deepest ? pathIdsTo(deepest, parentOf) : []);
  const filedPaths: string[][] = [];
  if (deepest && paths.has(deepest)) filedPaths.push(paths.get(deepest)!.split(' / '));
  else if (document.categoryPath.length > 0) filedPaths.push(document.categoryPath);
  for (const id of filedIds) {
    if (!onDeepestPath.has(id) && paths.has(id)) filedPaths.push(paths.get(id)!.split(' / '));
  }
  // The server's status, read as it is defined: `low_confidence` = not filed at
  // its final level yet (whoever filed it); `failed` = the AI could not file it.
  const filingNote =
    document.classificationStatus === 'failed'
      ? 'The AI couldn’t file it — choose its category'
      : isNeedsReview(document.classificationStatus)
        ? 'Not filed to its final level yet'
        : null;

  const canFile = branch !== null && !busy;
  const fileInBranch = () => {
    if (!branch) return;
    const target = paths.get(branch) ?? 'that category';
    resetActions();
    modify.mutate(
      {
        documentId: document.id,
        body: { categoryTagIds: withoutInstitutionLevel(nodes, pathIdsTo(branch, parentOf)) },
      },
      {
        onSuccess: (res) => {
          setBranch(null);
          const similar = res.similarDocuments?.filing ?? 0;
          setSaved(
            `Filed under ${target.split(' / ').join(' › ')}.` +
              (similar > 0
                ? ` Also filing ${similar} similar document${similar === 1 ? '' : 's'} (same name, other dates) the same way.`
                : ''),
          );
        },
      },
    );
  };

  const confirm = (field: 'Period' | 'Date of data', s: DateFieldSuggestion) => {
    resetActions();
    approve.mutate(s.proposal.id, { onSuccess: () => setSaved(`${field} confirmed: ${s.value}.`) });
  };
  const dismiss = (field: 'Period' | 'Date of data', s: DateFieldSuggestion) => {
    resetActions();
    reject.mutate(s.proposal.id, {
      onSuccess: () => setSaved(`${field} suggestion “${s.value}” rejected.`),
    });
  };

  const refileWithAi = () => {
    resetActions();
    reclassify.mutate(document.id, {
      onSuccess: () => setSaved('The AI is filing it again — this can take a minute.'),
    });
  };

  return (
    <Dialog
      title="Manage document"
      description={document.name}
      onClose={onClose}
      busy={busy}
      size="lg"
      footer={
        <>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : (
            <span className="text-xs text-muted-foreground">
              Changes are saved as yours and update the data room straight away.
            </span>
          )}
          <Button size="sm" variant="outline" onClick={onClose} disabled={busy}>
            Done
          </Button>
        </>
      }
    >
      <div className="space-y-6 text-sm">
        {/* Currently: where it is filed and under what date, at a glance. */}
        <section aria-label="Currently" className="space-y-3 rounded-md border bg-surface-2/50 p-3">
          <div>
            <p className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Filed under
              </span>
              {filedPaths.length > 0 ? <SetByChip setBy={document.filedBy} /> : null}
            </p>
            {filedPaths.length > 0 ? (
              filedPaths.map((segments, i) => (
                <nav key={i} aria-label="Filing path" className="mt-1">
                  <ol className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
                    {segments.map((seg, j) => (
                      <li key={`${seg}-${j}`} className="inline-flex items-center gap-x-1">
                        {j > 0 ? (
                          <Icon name="chevronRight" size={12} className="text-ink-3" />
                        ) : null}
                        <span className={j === segments.length - 1 ? 'font-semibold' : undefined}>
                          {seg}
                        </span>
                      </li>
                    ))}
                  </ol>
                </nav>
              ))
            ) : (
              <p className="mt-1 text-muted-foreground">Not filed in any category yet.</p>
            )}
            {filingNote ? (
              <p className="mt-1">
                <SuggestionChip>{filingNote}</SuggestionChip>
              </p>
            ) : null}
          </div>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <CurrentValue
              label="Period"
              value={document.period ?? null}
              setBy={document.periodSetBy}
              suggested={suggestions.period[0]}
            />
            <CurrentValue
              label="Date of data"
              value={document.dataDate ? formatDateOnly(document.dataDate) : null}
              setBy={document.dataDateSetBy}
              suggested={suggestions.date[0]}
            />
          </dl>
          {document.executionDate ? (
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">
                Executed {formatDateOnly(document.executionDate)}
              </span>{' '}
              — the date the last party signed, read from the contract.
            </p>
          ) : null}
        </section>

        {saved ? (
          <p
            ref={savedRef}
            tabIndex={-1}
            role="status"
            className="rounded bg-st-certified px-3 py-2 text-st-certified-fg outline-none"
          >
            {saved}
          </p>
        ) : null}

        {/* Date first: it is short, so the long category list below never
            pushes its Confirm buttons out of sight. */}
        <section aria-labelledby={dateId} className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <h3 id={dateId} className="font-medium">
                Change the date
              </h3>
              {dateHeadingAside}
            </div>
            <Button
              size="sm"
              variant={canSaveDate ? 'default' : 'secondary'}
              aria-disabled={!canSaveDate}
              title={
                canSaveDate
                  ? 'Save the period and date of data'
                  : periodChanged || dataDateChanged
                    ? 'Wait for the current change to finish'
                    : 'Change the period or the date of data first'
              }
              className={cn(!canSaveDate && 'cursor-not-allowed text-muted-foreground')}
              onClick={() => {
                if (canSaveDate) saveDate();
              }}
            >
              {dating.isPending ? 'Saving…' : 'Save date'}
            </Button>
          </div>
          <DateField
            label="Period"
            inputId={periodInputId}
            control={
              <select
                id={periodInputId}
                className={controlClass}
                value={periodValue}
                disabled={busy}
                onChange={(e) => setPeriodEdit({ from: periodNow, to: e.target.value })}
              >
                <option value="">Not set</option>
                {PERIOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
                {periodNow && !PERIOD_OPTIONS.some((o) => o.value === periodNow) ? (
                  <option value={periodNow}>{periodNow}</option>
                ) : null}
              </select>
            }
            suggestions={suggestions.period}
            busy={busy}
            loading={suggestionsLoading}
            onConfirm={(s) => confirm('Period', s)}
            onReject={(s) => dismiss('Period', s)}
          />
          <DateField
            label="Date of data"
            inputId={dataDateInputId}
            control={
              <>
                <input
                  id={dataDateInputId}
                  type="date"
                  className={controlClass}
                  value={dataDateValue}
                  disabled={busy}
                  aria-describedby={dataDateHintId}
                  onChange={(e) => setDataDateEdit({ from: dataDateNow, to: e.target.value })}
                />
                <p id={dataDateHintId} className="mt-1 text-xs text-muted-foreground">
                  The last day this document’s figures cover, e.g. Dec 31, 2025 for a 2025 P&amp;L.
                </p>
              </>
            }
            suggestions={suggestions.date}
            busy={busy}
            loading={suggestionsLoading}
            onConfirm={(s) => confirm('Date of data', s)}
            onReject={(s) => dismiss('Date of data', s)}
          />
        </section>

        {/* Filing: pick the branch; the one action sits at the top. */}
        <section aria-labelledby={filingId} className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 id={filingId} className="font-medium">
              Filing
            </h3>
            <Button
              size="sm"
              variant={canFile ? 'default' : 'secondary'}
              aria-disabled={!canFile}
              title={
                canFile
                  ? `File it under ${paths.get(branch!) ?? 'that category'}`
                  : branch
                    ? 'Wait for the current change to finish'
                    : 'Choose a category first'
              }
              className={cn(!canFile && 'cursor-not-allowed text-muted-foreground')}
              onClick={() => {
                if (canFile) fileInBranch();
              }}
            >
              {modify.isPending ? 'Filing…' : 'File in this branch'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {branch
              ? `Files it under ${(paths.get(branch) ?? 'that category').split(' / ').join(' › ')}, replacing where it is filed now.`
              : 'Choose the category it belongs in — search, or scroll the list.'}
          </p>
          {categories.isPending ? (
            <p className="text-muted-foreground">Loading categories…</p>
          ) : categories.error ? (
            <p className="text-destructive" role="alert">
              {errorMessage(categories.error) ?? 'Could not load the categories.'}
            </p>
          ) : (
            <div className="max-h-[40vh] overflow-y-auto rounded border p-2">
              <CategoryPicker
                categories={nodes}
                selected={branch ? new Set([branch]) : new Set()}
                onToggle={(id) => setBranch((prev) => (prev === id ? null : id))}
                disabled={modify.isPending}
              />
            </div>
          )}
          {canAddCategory || canReclassify ? (
            <div className="space-y-2 pt-1">
              <div className="flex flex-wrap items-center gap-2">
                {canAddCategory && !adding ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || categories.isPending}
                    onClick={() => {
                      resetActions();
                      setAdding(true);
                    }}
                  >
                    Add a new subfolder…
                  </Button>
                ) : null}
                {canReclassify ? (
                  <Button size="sm" variant="outline" disabled={busy} onClick={refileWithAi}>
                    {reclassify.isPending ? 'Sending…' : 'Re-file with AI'}
                  </Button>
                ) : null}
              </div>
              {canReclassify ? (
                <p className="text-xs text-muted-foreground">
                  “Re-file with AI” runs the AI on this document again. Anything you have filed
                  yourself stays where you put it.
                </p>
              ) : null}
              {adding ? (
                <AddCategoryForm
                  documentId={document.id}
                  add={addCategory}
                  categories={nodes}
                  paths={paths}
                  parentOf={parentOf}
                  filedIds={filedIds}
                  deepestFiledId={deepest}
                  disabled={busy && !addBusy}
                  onBusyChange={setAddBusy}
                  onCancel={() => setAdding(false)}
                  onDone={({ filedPath, created }) => {
                    setAdding(false);
                    setSaved(
                      `${created ? 'Subfolder added. ' : ''}Filed under ${filedPath.split(' / ').join(' › ')}.`,
                    );
                  }}
                />
              ) : null}
            </div>
          ) : null}
          {onEditTags ? (
            <p className="pt-1">
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                disabled={busy}
                onClick={onEditTags}
              >
                Put it in more than one folder…
              </button>
            </p>
          ) : null}
        </section>
      </div>
    </Dialog>
  );
}

/** "Set by AI" / "Set by a person"; nothing when no one has set it. */
function SetByChip({ setBy }: { setBy: SetBy | null | undefined }) {
  const label = setByLabel(setBy);
  return label ? (
    <span className="inline-flex rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
      {label}
    </span>
  ) : null;
}

function SuggestionChip({ children }: { children: string }) {
  return (
    <span className="inline-flex rounded-full bg-st-check px-2 py-0.5 text-xs font-medium text-st-check-fg">
      {children}
    </span>
  );
}

/** One "Currently" value, or the AI's suggestion awaiting confirmation. */
function CurrentValue({
  label,
  value,
  setBy,
  suggested,
}: {
  label: string;
  value: string | null;
  setBy: SetBy | null | undefined;
  suggested: DateFieldSuggestion | undefined;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 flex flex-wrap items-center gap-1.5">
        {value ? (
          <span className="font-semibold">{value}</span>
        ) : suggested ? (
          <span className="font-semibold">{suggested.value}</span>
        ) : (
          <span className="text-muted-foreground">Not set</span>
        )}
        {value ? <SetByChip setBy={setBy} /> : null}
        {!value && suggested ? <SuggestionChip>AI suggestion — confirm</SuggestionChip> : null}
      </dd>
    </div>
  );
}

/** A date field's control and its pending AI suggestions (Confirm / Reject). */
function DateField({
  label,
  inputId,
  control,
  suggestions,
  busy,
  loading,
  onConfirm,
  onReject,
}: {
  label: string;
  inputId: string;
  control: ReactNode;
  suggestions: DateFieldSuggestion[];
  busy: boolean;
  loading: boolean;
  onConfirm: (s: DateFieldSuggestion) => void;
  onReject: (s: DateFieldSuggestion) => void;
}) {
  return (
    <div className="rounded border px-3 py-2">
      <label htmlFor={inputId} className="mb-1 block font-medium">
        {label}
      </label>
      {control}
      {suggestions.length > 0 ? (
        <ul className="mt-2 space-y-1.5" aria-label={`${label} suggestions`}>
          {suggestions.map((s) => (
            <li key={s.proposal.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0">
                <span className="font-semibold">{s.value}</span>{' '}
                <SuggestionChip>{`AI suggestion — confirm · ${suggestionCertainty(s.proposal)}`}</SuggestionChip>
              </span>
              <span className="flex shrink-0 gap-1.5">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => onConfirm(s)}
                  aria-label={`Confirm ${label.toLowerCase()} ${s.value}`}
                >
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => onReject(s)}
                  aria-label={`Reject ${label.toLowerCase()} ${s.value}`}
                >
                  Reject
                </Button>
              </span>
            </li>
          ))}
        </ul>
      ) : loading ? (
        <p className="mt-1 text-xs text-muted-foreground">Checking for AI suggestions…</p>
      ) : null}
    </div>
  );
}
